// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/SOPGenerator.js

import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { ActionGroup, Button } from "../design-system/components";
import "./SOPGenerator.css";

const REQUEST_TIMEOUT_MS = 30000;
const TEMPLATE_PREFILLS = {
  "tmpl-new-hire-onboarding": {
    objective:
      "Standardize how we onboard new hires from offer acceptance through the first 90 days.",
    processName: "New Hire Onboarding",
    steps: [
      "Receive signed offer letter and create employee profile in HR system.",
      "Send welcome email with start date, schedule, and required paperwork.",
      "Complete new hire paperwork, I-9, and tax forms on Day 1.",
      "Provision accounts, system access, and required equipment.",
      "Conduct role-specific training and shadowing during first 2 weeks.",
      "Schedule 30/60/90-day check-ins and performance reviews.",
    ],
    compliance: [
      "HR recordkeeping and personnel file requirements.",
      "Right-to-work / I-9 documentation and retention.",
      "Data privacy and IT security policy acknowledgment.",
      "Workplace safety orientation and training records.",
    ],
  },
  "tmpl-gmp-batch-production": {
    objective:
      "Document a complete GMP batch production record for each manufactured lot.",
    processName: "GMP Batch Production Record",
    steps: [
      "Verify approved master manufacturing record (MMR) and effective revision.",
      "Issue a unique batch / lot number and print batch record packet.",
      "Dispense and verify raw materials against the MMR and COAs.",
      "Set up and line-clear equipment; document pre-use checks and status labels.",
      "Execute each manufacturing step (mixing, blending, filling) with in-process checks.",
      "Record yields, reconciliations, and deviations or unplanned events.",
      "Perform final QA review and release / reject decision.",
    ],
    compliance: [
      "21 CFR Part 111 / Part 117 GMP documentation requirements.",
      "Data integrity (ALCOA+) for entries, corrections, and signatures.",
      "Equipment cleaning / line clearance verification before and after runs.",
      "Lot traceability from raw materials to finished goods release.",
    ],
  },
  "tmpl-cleaning-sanitation": {
    objective:
      "Define routine and deep-cleaning procedures for production and storage areas.",
    processName: "Cleaning & Sanitation",
    steps: [
      "Identify areas, zones, and surfaces that require routine cleaning.",
      "Select approved cleaning agents, sanitizers, and required PPE.",
      "Perform pre-cleaning line clearance and equipment shutdown.",
      "Execute cleaning steps (pre-rinse, wash, rinse, sanitize, dry).",
      "Document cleaning on logs, including date, time, and responsible person.",
      "Conduct periodic deep-cleaning and verification inspections.",
    ],
    compliance: [
      "GMP requirements for cleaning, sanitation, and cross-contamination control.",
      "Chemical handling, dilution, and storage instructions.",
      "Environmental health & safety requirements for PPE and ventilation.",
      "Recordkeeping for cleaning logs and verification checks.",
    ],
  },
  "tmpl-packaging-label-review": {
    objective:
      "Standardize how labels and packaging are reviewed, approved, and released.",
    processName: "Packaging & Label Review",
    steps: [
      "Receive draft label artwork and packaging components from Marketing or Supplier.",
      "Verify product name, strength, net contents, and required statements.",
      "Review ingredient list, allergens, and nutrition / supplement facts panel.",
      "Check claims, disclaimers, and required regulatory statements.",
      "Confirm barcodes, lot / expiry space, and version control details.",
      "Route for cross-functional approval (Regulatory, QA, Marketing) before release.",
    ],
    compliance: [
      "Applicable labeling regulations (e.g., FDA 21 CFR 101 for foods/supplements).",
      "Prohibited disease claims and required disclaimers.",
      "Artwork version control and approved vendor copies.",
      "Retention of approved proofs and change-control history.",
    ],
  },
  "tmpl-customer-support-escalation": {
    objective:
      "Provide a consistent flow for handling, escalating, and resolving customer issues.",
    processName: "Customer Support Escalation",
    steps: [
      "Capture customer issue details in the ticketing system.",
      "Classify severity, impact, and category (billing, technical, product, etc.).",
      "Apply first-line troubleshooting and standard responses.",
      "Escalate to Tier 2 / specialized teams based on defined criteria.",
      "Communicate status updates and resolution to the customer.",
      "Log root cause, resolution, and any required follow-up actions.",
    ],
    compliance: [
      "Service level targets (response and resolution times).",
      "Customer data privacy and secure handling in communications.",
      "Documentation requirements for tickets and escalations.",
      "Requirements for complaint / adverse event reporting, if applicable.",
    ],
  },
  "tmpl-capa": {
    objective:
      "Define a structured Corrective and Preventive Action (CAPA) process.",
    processName: "Corrective & Preventive Action (CAPA)",
    steps: [
      "Log deviation, complaint, or audit finding and assign a CAPA number.",
      "Perform initial assessment and containment, if needed.",
      "Investigate and document root cause using an approved methodology.",
      "Define corrective actions to address the immediate issue.",
      "Define preventive actions to reduce likelihood of recurrence.",
      "Implement actions, verify effectiveness, and formally close the CAPA.",
    ],
    compliance: [
      "GMP / ISO quality system requirements for CAPA.",
      "Timeframes for CAPA initiation, investigation, and closure.",
      "Documentation of investigations, decisions, and approvals.",
      "Trending and management review of CAPA metrics.",
    ],
  },
  "tmpl-complaint-handling": {
    objective:
      "Standardize how product complaints and adverse events are captured and investigated.",
    processName: "Product Complaint Handling",
    steps: [
      "Receive complaint and capture required information from the reporter.",
      "Classify complaint type (quality, safety, label, service, other).",
      "Assess for potential adverse event or safety signal.",
      "Initiate investigation and collect supporting samples or data.",
      "Document findings, root cause, and required corrective actions.",
      "Respond to complainant as appropriate and close the record.",
    ],
    compliance: [
      "Regulatory requirements for complaint files and retention.",
      "Adverse event reporting obligations and timelines, if applicable.",
      "Data privacy considerations for personally identifiable information.",
      "Trend analysis and escalation for recurring issues.",
    ],
  },
  "tmpl-supplier-qualification": {
    objective:
      "Establish a consistent process for qualifying, onboarding, and monitoring suppliers.",
    processName: "Supplier Qualification & Onboarding",
    steps: [
      "Identify new supplier need and request qualification.",
      "Issue and review supplier questionnaire and supporting documentation.",
      "Conduct risk assessment based on material criticality and supplier history.",
      "Perform on-site or remote audit when required.",
      "Approve supplier, set status in vendor list, and communicate requirements.",
      "Periodically re-evaluate performance, quality, and compliance.",
    ],
    compliance: [
      "Documented approval status for all critical suppliers.",
      "Quality agreements and expectations for GMP / quality systems.",
      "Requirements for supplier audits and re-evaluation frequency.",
      "Traceability of materials back to qualified suppliers.",
    ],
  },
  "tmpl-equipment-calibration": {
    objective:
      "Define how critical equipment is calibrated, maintained, and documented.",
    processName: "Equipment Calibration & Maintenance",
    steps: [
      "Create and maintain an inventory of critical equipment and instruments.",
      "Assign calibration and preventive maintenance frequencies.",
      "Schedule work and coordinate with internal teams or external vendors.",
      "Perform calibration / maintenance and document results and status.",
      "Update status labels (In-Service, Out-of-Service, Due for Calibration).",
      "Review trends and address repeated failures or out-of-tolerance results.",
    ],
    compliance: [
      "GMP / quality system requirements for equipment qualification and calibration.",
      "Use of only calibrated equipment for release testing and critical measurements.",
      "Retention of calibration certificates and maintenance records.",
      "Change-control when equipment is moved, modified, or replaced.",
    ],
  },
  "tmpl-change-control": {
    objective:
      "Provide a single controlled process for proposing and implementing changes.",
    processName: "Change Control",
    steps: [
      "Submit change request with description, justification, and scope.",
      "Assess impact on quality, safety, validation, labeling, and customers.",
      "Define required actions, responsibilities, and implementation plan.",
      "Obtain cross-functional review and formal approval before execution.",
      "Implement change and update affected documents, systems, or equipment.",
      "Verify effectiveness and close the change record.",
    ],
    compliance: [
      "Quality system requirements for documented change control.",
      "Risk assessment and mitigation for proposed changes.",
      "Version control for procedures, batch records, and labels.",
      "Communication and training requirements for impacted staff.",
    ],
  },
};

const PLACEHOLDER_STEP_VALUES = new Set([
  "",
  "1",
  "1.",
  "2",
  "2.",
  "3",
  "3.",
  "-",
  "--",
  ".",
  "...",
  "n/a",
  "na",
  "none",
  "null",
  "tbd",
  "todo",
  "?",
]);

function looksLikeUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim()
  );
}

function normalizeDynamicEntries(items) {
  return (Array.isArray(items) ? items : []).map((item) => String(item || ""));
}

function sanitizeStepEntry(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  if (PLACEHOLDER_STEP_VALUES.has(trimmed.toLowerCase())) return "";
  return trimmed;
}

function getMeaningfulSteps(items) {
  return normalizeDynamicEntries(items)
    .map((item) => sanitizeStepEntry(item))
    .filter(Boolean);
}

function getMeaningfulCompliance(items) {
  return normalizeDynamicEntries(items)
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

function dispatchSopGeneratedEvent(detail = {}) {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("oasys:sop-document-control-changed", {
      detail: {
        type: "sop_generated",
        source: "sop_generator",
        ...detail,
      },
    })
  );
}

export default function SOPGenerator({
  apiBaseUrl,
  getAccessToken,
  userProfile = null,
}) {
  const { user, getAccessTokenSilently } = useAuth0();
  const userEmail = user?.email || "";

  const normalizedBase = useMemo(() => {
    const base = (apiBaseUrl || "").trim();
    if (base.endsWith("/")) return base.slice(0, -1);
    return base;
  }, [apiBaseUrl]);

  const tenantId = useMemo(() => {
    const candidates = [
      userProfile?.tenant_id,
      userProfile?.tenantId,
      userProfile?.app_metadata?.tenant_id,
      userProfile?.app_metadata?.tenantId,
      process.env.REACT_APP_TENANT_ID,
    ];
    const match = candidates.find((value) => looksLikeUuid(value));
    return match || "";
  }, [userProfile]);

  const resolvedUserId = useMemo(() => {
    const candidates = [
      userProfile?.user_id,
      userProfile?.userId,
      userProfile?.id,
      userProfile?.app_metadata?.user_id,
      userProfile?.app_metadata?.userId,
      user?.["https://nutrx/user_id"],
      user?.["https://oasys/user_id"],
      user?.user_id,
      process.env.REACT_APP_USER_ID,
    ];
    const match = candidates.find((value) => looksLikeUuid(value));
    return match || "";
  }, [userProfile, user]);

  const HEALTHCHECK_URL = `${normalizedBase}/health`;
  const GENERATE_SOP_URL = `${normalizedBase}/generate-sop`;
  const TASK_STATUS_URL = `${normalizedBase}/tasks`;

  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const stepLabels = ["Objective", "Process Name", "Steps", "Compliance"];

  const [objective, setObjective] = useState("");
  const [processName, setProcessName] = useState("");
  const [steps, setSteps] = useState([""]);
  const [compliance, setCompliance] = useState([""]);

  const [status, setStatus] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [backendHealthy, setBackendHealthy] = useState(null);


  const fetchToken = useCallback(async () => {
    if (typeof getAccessToken === "function") {
      return await getAccessToken();
    }

    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    return await getAccessTokenSilently(
      audience ? { authorizationParams: { audience } } : undefined
    );
  }, [getAccessToken, getAccessTokenSilently]);

  const getAuthHeaders = useCallback(
    async ({ includeJson = true } = {}) => {
      const token = await fetchToken();

      const headers = {};
      if (includeJson) {
        headers["Content-Type"] = "application/json";
      }
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (tenantId) {
        headers["X-Tenant-Id"] = tenantId;
      }
      if (resolvedUserId) {
        headers["X-User-Id"] = resolvedUserId;
      }

      return headers;
    },
    [fetchToken, tenantId, resolvedUserId]
  );

  useEffect(() => {
    let mounted = true;

    async function runHealthcheck() {
      if (!normalizedBase) {
        if (mounted) setBackendHealthy(false);
        return;
      }

      try {
        const res = await axios.get(HEALTHCHECK_URL, {
          timeout: REQUEST_TIMEOUT_MS,
        });
        if (mounted) {
          setBackendHealthy(res.data?.status === "ok");
        }
      } catch {
        if (mounted) {
          setBackendHealthy(false);
        }
      }
    }

    runHealthcheck();

    return () => {
      mounted = false;
    };
  }, [HEALTHCHECK_URL, normalizedBase]);

  useEffect(() => {
    try {
      let templateId = null;

      if (typeof window !== "undefined") {
        templateId =
          window.localStorage.getItem("oasys_sop_template_id") || null;

        if (!templateId) {
          const hash = window.location.hash || "";
          const idx = hash.indexOf("?");
          if (idx !== -1) {
            const qs = hash.slice(idx + 1);
            const params = new URLSearchParams(qs);
            templateId = params.get("templateId") || null;
          }
        }
      }

      if (!templateId) return;

      const tpl = TEMPLATE_PREFILLS[templateId];

      if (typeof window !== "undefined") {
        window.localStorage.removeItem("oasys_sop_template_id");
      }

      if (!tpl) return;

      setActiveTemplateId(templateId);
      setObjective(tpl.objective || "");
      setProcessName(tpl.processName || "");
      setSteps(
        Array.isArray(tpl.steps) && tpl.steps.length > 0 ? tpl.steps : [""]
      );
      setCompliance(
        Array.isArray(tpl.compliance) && tpl.compliance.length > 0
          ? tpl.compliance
          : [""]
      );
      setCurrentStep(0);
    } catch (e) {
      console.error("Error loading SOP template selection:", e);
    }
  }, []);

  const templateLabel =
    activeTemplateId && TEMPLATE_PREFILLS[activeTemplateId]
      ? TEMPLATE_PREFILLS[activeTemplateId].processName ||
        TEMPLATE_PREFILLS[activeTemplateId].objective ||
        activeTemplateId
      : "";

  const updateList = (arr, setter, idx, val) => {
    const copy = [...(Array.isArray(arr) ? arr : [])];
    copy[idx] = val;
    setter(copy);
  };

  const addItem = (setter) =>
    setter((arr) => [...(Array.isArray(arr) ? arr : []), ""]);

  const removeItem = (arr, setter, idx) => {
    if (!Array.isArray(arr)) return;
    const filtered = arr.filter((_, i) => i !== idx);
    setter(filtered.length > 0 ? filtered : [""]);
  };

  const submitGenerationRequest = useCallback(
    async (payload) => {
      const headers = await getAuthHeaders();

      return axios.post(GENERATE_SOP_URL, payload, {
        headers,
        timeout: REQUEST_TIMEOUT_MS,
      });
    },
    [GENERATE_SOP_URL, getAuthHeaders]
  );

  const handleSubmit = async () => {
    setError("");
    setStatus("");
    setResult(null);

    if (!normalizedBase) {
      setError("SOP Generator requires apiBaseUrl.");
      return;
    }

    const sanitizedSteps = getMeaningfulSteps(steps);
    const sanitizedCompliance = getMeaningfulCompliance(compliance);

    const payload = {
      objective: objective.trim(),
      process_name: processName.trim(),
      steps: sanitizedSteps,
      compliance_requirements: sanitizedCompliance,
      user_email: userEmail || undefined,
      template_id: activeTemplateId || undefined,
    };

    setLoading(true);
    try {
      const { data } = await submitGenerationRequest(payload);
      setStatus("in progress");
      setResult({ taskId: data.task_id });
    } catch (err) {
      const statusCode = err?.response?.status;

      if (statusCode === 402) {
        setError(
          "SOP generation is unavailable for this account under the current commercial configuration."
        );
      } else if (statusCode === 401) {
        setError("Unauthorized. Please refresh and sign in again.");
      } else if (statusCode === 403) {
        setError(
          "Forbidden. Verify the generator route accepts your bearer token and tenant headers."
        );
      } else if (statusCode === 404) {
        setError(
          "Generator route not found. Verify the active SOP generation endpoint path."
        );
      } else {
        console.error(err);
        setError("Unexpected error submitting SOP request");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!result?.taskId || status !== "in progress") return;

    let mounted = true;

    const iv = setInterval(async () => {
      try {
        const headers = await getAuthHeaders({ includeJson: false });
        const { data } = await axios.get(
          `${TASK_STATUS_URL}/${result.taskId}`,
          {
            headers,
            timeout: REQUEST_TIMEOUT_MS,
          }
        );

        if (!mounted) return;

        const taskStatus = data.status || data.state || null;
        const inner = data.result || data || {};

        const docxUrl =
          inner.docx_url ||
          inner.docx ||
          (inner.urls && inner.urls.docx) ||
          data.docx_url ||
          null;

        const pdfUrl =
          inner.pdf_url ||
          inner.pdf ||
          (inner.urls && inner.urls.pdf) ||
          data.pdf_url ||
          null;

        if (
          taskStatus &&
          taskStatus !== "SUCCESS" &&
          taskStatus !== "completed"
        ) {
          return;
        }

        if (docxUrl || pdfUrl) {
          setResult({
            docx_url: docxUrl,
            pdf_url: pdfUrl,
          });
          setStatus("completed");

          dispatchSopGeneratedEvent({
            task_id: result.taskId,
            has_pdf: Boolean(pdfUrl),
            has_docx: Boolean(docxUrl),
          });

          clearInterval(iv);
          return;
        }

        if (taskStatus === "SUCCESS" || taskStatus === "completed") {
          setStatus("completed");

          dispatchSopGeneratedEvent({
            task_id: result.taskId,
            has_pdf: Boolean(pdfUrl),
            has_docx: Boolean(docxUrl),
          });

          clearInterval(iv);
        }
      } catch (e) {
        console.error(e);
        setError("Error checking status");
        clearInterval(iv);
      }
    }, 2000);

    return () => {
      mounted = false;
      clearInterval(iv);
    };
  }, [result?.taskId, status, TASK_STATUS_URL, getAuthHeaders]);

  const canNext = () => {
    switch (currentStep) {
      case 0:
        return objective.trim() !== "";
      case 1:
        return processName.trim() !== "";
      case 2:
        return true;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < stepLabels.length - 1 && canNext()) {
      setCurrentStep((i) => i + 1);
    } else if (currentStep === stepLabels.length - 1) {
      handleSubmit();
    }
  };

  return (
    <div className="sop-generator">
      {templateLabel && (
        <div className="template-indicator">
          <span>📌 Template applied:&nbsp;</span>
          <strong>{templateLabel}</strong>
        </div>
      )}

      {backendHealthy === false && (
        <div className="status error">
          Something went wrong. Please refresh or try again later.
        </div>
      )}

      <ul className="step-indicator">
        {stepLabels.map((lbl, idx) => (
          <li
            key={idx}
            className={
              idx === currentStep ? "active" : idx < currentStep ? "completed" : ""
            }
          >
            {lbl}
          </li>
        ))}
      </ul>

      <div className="wizard-step">
        {currentStep === 0 && (
          <div className="field">
            <label>
              Objective
              <small>Describe the outcome this SOP is meant to standardize.</small>
            </label>
            <input
              type="text"
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
            />
          </div>
        )}

        {currentStep === 1 && (
          <div className="field">
            <label>
              Process Name
              <small>Name the procedure the team will recognize and use.</small>
            </label>
            <input
              type="text"
              value={processName}
              onChange={(e) => setProcessName(e.target.value)}
            />
          </div>
        )}

        {currentStep === 2 && (
          <fieldset className="field">
            <legend>
              Steps
              <br />
              <small>
                Add draft steps if you know them. You can also leave this blank and OASYS will infer a procedure for you.
              </small>
            </legend>
            {steps.map((step, i) => (
              <div key={i} className="dynamic-list">
                <input
                  type="text"
                  value={step}
                  placeholder="Optional draft step"
                  onChange={(e) => updateList(steps, setSteps, i, e.target.value)}
                />
                {steps.length > 1 && (
                  <Button
                    variant="danger"
                    size="small"
                    className="sop-generator__remove-button"
                    onClick={() => removeItem(steps, setSteps, i)}
                    aria-label={`Remove step ${i + 1}`}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            <Button
              variant="secondary"
              size="small"
              onClick={() => addItem(setSteps)}
            >
              Add Step
            </Button>
          </fieldset>
        )}

        {currentStep === 3 && (
          <fieldset className="field">
            <legend>
              Compliance Requirements
              <br />
              <small>
                Add control points, quality expectations, or regulatory requirements
                that should be reflected in the final SOP. Leave blank if none need
                to be specified.
              </small>
            </legend>
            {compliance.map((req, i) => (
              <div key={i} className="dynamic-list">
                <input
                  type="text"
                  value={req}
                  placeholder="Optional compliance requirement"
                  onChange={(e) =>
                    updateList(compliance, setCompliance, i, e.target.value)
                  }
                />
                {compliance.length > 1 && (
                  <Button
                    variant="danger"
                    size="small"
                    className="sop-generator__remove-button"
                    onClick={() => removeItem(compliance, setCompliance, i)}
                    aria-label={`Remove compliance requirement ${i + 1}`}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            <Button
              variant="secondary"
              size="small"
              onClick={() => addItem(setCompliance)}
            >
              Add Requirement
            </Button>
          </fieldset>
        )}
      </div>

      {error && <div className="status error">{error}</div>}

      <ActionGroup
        align="end"
        density="compact"
        className="wizard-nav"
      >
        {currentStep > 0 ? (
          <Button
            variant="secondary"
            size="medium"
            onClick={() => setCurrentStep((i) => i - 1)}
          >
            Back
          </Button>
        ) : null}

        <Button
          variant="primary"
          size="medium"
          onClick={handleNext}
          disabled={!canNext() || loading}
          loading={loading && currentStep === stepLabels.length - 1}
          loadingLabel="Generating…"
        >
          {currentStep < stepLabels.length - 1 ? "Next" : "Generate SOP"}
        </Button>
      </ActionGroup>

      {status === "in progress" && (
        <div className="status status--active">
          Generating your SOP… please wait.
        </div>
      )}

      {status === "completed" && result && (
        <div className="result">
          <div className="status status--success">✅ Your SOP draft is ready!</div>
          <div className="download-links">
            {result.pdf_url ? (
              <a href={result.pdf_url} target="_blank" rel="noopener noreferrer">
                📄 Download PDF
              </a>
            ) : (
              <span className="download-link-disabled">📄 PDF unavailable</span>
            )}

            {result.docx_url ? (
              <a
                href={result.docx_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                📝 Download Word
              </a>
            ) : (
              <span className="download-link-disabled">📝 Word unavailable</span>
            )}
          </div>
        </div>
      )}

    </div>
  );
}